import {Lang} from 'yougile-lang'
import {logger} from 'yougile-util'
import {
  createDefaultReports,
  createReportHub,
  DataObjectId,
  Hub,
  Report,
  reportsContainerId,
  SandboxTree
} from 'yougile-model'
import {GanttDraft} from '@extensions/gantt-draft'
import {globals} from '@extensions/globals-base'
import {PermissionsChecker} from './permissions-checker'

export class ReportsClient {
  constructor(private box: SandboxTree) {}

  public getAggregatedReport(): Report {
    if (!this.box.has(reportsContainerId)) {
      return undefined
    }
    const hubs = this.box.getChildrenHubs(reportsContainerId)
    const userId = globals.currentUserId
    return hubs
      .filter(h => !!h.data.report)
      .map(h => ({...h.data.report, id: h.id}))
      .filter(r => r.aggregatedForUsers?.includes(userId))[0]
  }

  public createExampleReportsIfNeeded(): void {
    const all = this.getAllReports(true)
    const oldDefault = all.find(h => h.isExample)
    if (!oldDefault) {
      logger.log('Creating example reports…')
      const examples = createDefaultReports(Lang.msg, globals.currentUserId)
      examples.forEach(r => {
        this.saveReport(r)
      })
    }
  }

  public getAllReports(withDeleted = false): Report[] {
    if (!this.box.has(reportsContainerId)) {
      return []
    }
    const hubs = this.box.getChildrenHubs(reportsContainerId, {withDeleted})
    const userId = globals.currentUserId
    const isAdmin = PermissionsChecker.isAdmin({userId, box: this.box})
    return hubs
      .filter(h => !!h.data.report)
      .map(h => ({...h.data.report, id: h.id}))
      .filter(
        r => (!r.availableTo && isAdmin) || r.availableTo?.indexOf(userId) > -1
      )
  }

  public deleteReport(reportId: DataObjectId): void {
    const rep = this.box.get(reportId) as Hub
    if (rep && rep.data.report) {
      this.box.remove(rep)
      GanttDraft.planEntitySubtree(rep.id, this.box)
      if (rep.data.report.schedule) {
        rep.data.report.schedule.enabled = false
        rep.commit()
      }
    }
  }

  public saveReport(newReport: Report): void {
    if (!newReport) {
      return
    }
    if (!this.box.has(reportsContainerId)) {
      logger.error('No reports container')
      return
    }
    const hubs = this.box.getChildrenHubs(reportsContainerId)
    const existent = hubs.find(h => h.id === newReport.id)
    if (existent) {
      existent.data.report = newReport
      existent.commit()
    } else {
      this.box.add(
        createReportHub(this.box.companyId, newReport),
        reportsContainerId,
        false,
        false
      )
    }
  }
}
